// MatchPlayView - Main match generation view
// Created by Taulant Geci

import React, { useState, useEffect } from 'react';
import { MatchGeneratorController } from '../modules/MatchGeneratorController';
import { Player, Court, Match } from '../models/types';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser, 
  faPlus, 
  faTimes, 
  faUsers, 
  faMapMarkerAlt, 
  faTrophy, 
  faList, 
  faDice, 
  faWandMagicSparkles, 
  faUndo, 
  faUserSlash, 
  faEdit,
  faCheckCircle,
  faCog,
  faUserPlus
} from '@fortawesome/free-solid-svg-icons';
import { MatchResultModal } from './MatchResultModal';

interface MatchPlayViewProps {
  controller: MatchGeneratorController;
}

// Draggable Player Component
interface DraggablePlayerProps {
  player: {
    id: string;
    name: string;
  };
  matchId: string;
  playerIndex: number;
  isWinner: boolean;
  controller: MatchGeneratorController;
}

const DraggablePlayer: React.FC<DraggablePlayerProps> = ({ 
  player, 
  matchId, 
  playerIndex, 
  isWinner,
  controller 
}) => {
  const [isDragTarget, setIsDragTarget] = useState(false);
  
  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'match-player',
      playerId: player.id,
      matchId,
      playerIndex
    }));
    e.dataTransfer.effectAllowed = 'move';
  };
  
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragTarget(true);
  };
  
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragTarget(false);
  };
  
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragTarget(false);
    
    try {
      const draggedData = JSON.parse(e.dataTransfer.getData('application/json'));
      
      if (draggedData.type === 'match-player') {
        // Swap with another match player
        if (draggedData.matchId !== matchId || draggedData.playerIndex !== playerIndex) {
          controller.swapPlayers(
            draggedData.matchId,
            draggedData.playerIndex,
            matchId,
            playerIndex
          );
        }
      } else if (draggedData.type === 'unmatched-player') {
        // Swap with unmatched player
        controller.swapPlayerWithUnmatched(
          matchId,
          playerIndex,
          draggedData.playerIndex
        );
      }
    } catch (error) {
      console.error('Failed to parse drag data:', error);
    }
  };
  
  return (
    <span
      className={`draggable-player ${isDragTarget ? 'drag-target' : ''} ${isWinner ? 'winner' : ''}`}
      draggable
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {player.name}
      {isWinner && (
        <FontAwesomeIcon icon={faTrophy} className="winner-icon ms-1" />
      )}
    </span>
  );
};

// Draggable Unmatched Player Component
interface DraggableUnmatchedPlayerProps {
  player: {
    id: string;
    name: string;
  };
  playerIndex: number;
  controller: MatchGeneratorController;
}

const DraggableUnmatchedPlayer: React.FC<DraggableUnmatchedPlayerProps> = ({ 
  player, 
  playerIndex, 
  controller 
}) => {
  const [isDragTarget, setIsDragTarget] = useState(false);
  
  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'unmatched-player',
      playerId: player.id,
      playerIndex
    }));
    e.dataTransfer.effectAllowed = 'move';
  };
  
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragTarget(true);
  };
  
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragTarget(false);
  };
  
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragTarget(false);
    
    try {
      const draggedData = JSON.parse(e.dataTransfer.getData('application/json'));
      
      if (draggedData.type === 'match-player') {
        // Swap with match player
        controller.swapPlayerWithUnmatched(
          draggedData.matchId,
          draggedData.playerIndex,
          playerIndex
        );
      } else if (draggedData.type === 'unmatched-player') {
        // Swap with another unmatched player
        if (draggedData.playerIndex !== playerIndex) {
          controller.swapUnmatchedPlayers(
            draggedData.playerIndex,
            playerIndex
          );
        }
      }
    } catch (error) {
      console.error('Failed to parse drag data:', error);
    }
  };
  
  return (
    <span
      className={`draggable-unmatched-player ${isDragTarget ? 'drag-target' : ''}`}
      draggable
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <FontAwesomeIcon icon={faUser} className="me-1" />
      {player.name}
    </span>
  );
};

export const MatchPlayView: React.FC<MatchPlayViewProps> = ({ controller }) => {
  const { t } = useTranslation();
  const [state, setState] = useState(controller.getState());
  const [newPlayerName, setNewPlayerName] = useState('');
  const [modalMatch, setModalMatch] = useState<Match | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const unsubscribe = controller.subscribe(() => {
      setState(controller.getState());
    });
    return unsubscribe;
  }, [controller]);

  const handleAddPlayer = () => {
    if (newPlayerName.trim()) {
      const success = controller.addPlayer(newPlayerName.trim());
      if (success) {
        setNewPlayerName('');
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAddPlayer();
    }
  };

  const handleGenerateMatches = () => {
    controller.generateMatches();
  };

  const handleResetMatches = () => {
    controller.resetMatches();
  };

  const handleRemovePlayer = (playerId: string) => {
    controller.togglePlayerActive(playerId);
  };

  const handleGroupSelect = (groupId: string | undefined) => {
    controller.selectGroup(groupId);
    if (groupId) {
      controller.loadGroupPlayers();
    }
  };

  const handleOpenResultModal = (match: Match) => {
    setModalMatch(match);
    setIsModalOpen(true);
  };

  const handleCloseResultModal = () => {
    setIsModalOpen(false);
    setModalMatch(null);
  };

  const handleResultSaved = () => {
    // Force re-render by updating state
    setState(controller.getState());
  };

  const activePlayersCount = state.activePlayers.length;

  return (
    <div className="match-play-view">
      {/* Modal for match results */}
      {isModalOpen && modalMatch && (
        <MatchResultModal
          match={modalMatch}
          controller={controller}
          isOpen={isModalOpen}
          onClose={handleCloseResultModal}
          onSaved={handleResultSaved}
        />
      )}

      <div className="main-content">
        {/* Game Settings Section */}
        <div className="section-header">
          <div className="section-title">
            <FontAwesomeIcon icon={faCog} className="section-icon" />
            {t('gameSettings')}
          </div>
        </div>

        {/* Courts Section */}
        <div className="court-section">
          <div className="court-selector card-ios">
            <div className="court-header">
              <div className="court-label">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="section-icon" />
                {t('courts')}
              </div>
              <div className="court-count-badge">
                <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: '0.75rem' }} />
                {state.courts.length} {t('courtsSelected')}
              </div>
            </div>

            <div className="court-picker">
              <select 
                value={state.courts.length} 
                onChange={(e) => controller.setCourtCount(parseInt(e.target.value))}
                className="form-select-dark"
              >
                {[1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16].map(num => (
                  <option key={num} value={num}>
                    {num} {num === 1 ? t('court') : t('courts')}
                  </option>
                ))}
              </select>
            </div>

            <div className="court-grid">
              {state.courts.map((court, index) => (
                <div key={court.id} className="court-circle">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="court-icon" />
                  <div>{t('court')} {index + 1}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Players Section */}
        <div className="section-header">
          <div className="section-title">
            <FontAwesomeIcon icon={faUsers} className="section-icon" />
            {t('players')}
          </div>
          <div className="section-badge">
            <FontAwesomeIcon icon={faUserPlus} />
            {activePlayersCount} {t('added')}
          </div>
        </div>

        <div className="player-section">
          <div className="player-group-selector card-ios">
            {/* Group Selection */}
            {state.playerGroups.length > 0 && (
              <div className="group-selection-row">
                <div className="input-group mb-3">
                  <span className="input-group-text">
                    <FontAwesomeIcon icon={faUsers} />
                  </span>
                  <select 
                    className="form-control"
                    value={state.selectedGroupID || ''}
                    onChange={(e) => handleGroupSelect(e.target.value || undefined)}
                  >
                    <option value="">{t('selectGroupOptional')}</option>
                    {state.playerGroups.map(group => (
                      <option key={group.id} value={group.id}>
                        {group.name} ({controller.getPlayersInGroup(group.id).length} {t('players')})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Manual Player Entry */}
            <div className="add-player-form">
              <div className="input-group-dark">
                <span className="input-group-text-dark">
                  <FontAwesomeIcon icon={faUser} />
                </span>
                <input
                  type="text"
                  value={newPlayerName}
                  onChange={(e) => setNewPlayerName(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={t('newPlayerName') || 'Spieler hinzufügen...'}
                  className="add-player-input"
                />
              </div>
              <button 
                onClick={handleAddPlayer}
                className="add-button"
                disabled={!newPlayerName.trim()}
                title={t('addPlayer') || 'Add player'}
              >
                <FontAwesomeIcon icon={faPlus} />
              </button>
            </div>

            {/* Active Players Display */}
            <div className="current-players">
              {activePlayersCount === 0 ? (
                <div className="empty-players-message">
                  <FontAwesomeIcon icon={faUserSlash} className="empty-icon" />
                  <p>{t('selectGroupOrAddPlayers')}</p>
                </div>
              ) : (
                <div className="player-pills">
                  {state.activePlayers.map((player: Player) => (
                    <div key={player.id} className="player-pill">
                      <FontAwesomeIcon icon={faUser} style={{ fontSize: '0.75rem' }} />
                      {player.name}
                      <button 
                        className="player-pill-remove"
                        onClick={() => handleRemovePlayer(player.id)}
                        aria-label={t('removePlayer') || 'Remove player'}
                        title={t('removePlayer') || 'Remove player'}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Unmatched Players Section */}
        {state.unmatchedPlayers.length > 0 && (
          <div className="unmatched-players-section">
            <div className="section-header">
              <div className="section-title">
                <FontAwesomeIcon icon={faUserSlash} className="section-icon" />
                {t('waitingPlayers')}
              </div>
              <div className="section-badge">
                <FontAwesomeIcon icon={faUsers} />
                {state.unmatchedPlayers.length}
              </div>
            </div>

            <div className="player-pills-container card-ios">
              <div className="player-pills">
                {state.unmatchedPlayers.map((player, index) => (
                  <div key={`unmatched-${player.id}`} className="player-pill player-pill-waiting">
                    <DraggableUnmatchedPlayer
                      player={player}
                      playerIndex={index}
                      controller={controller}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Generated Matches Section */}
        <div className="matches-section">
          <div className="section-header">
            <div className="section-title">
              <FontAwesomeIcon icon={faTrophy} className="section-icon" />
              {t('generatedMatches')}
            </div>
            {state.generatedMatches.length > 0 && (
              <div className="section-badge">
                <FontAwesomeIcon icon={faList} />
                {state.generatedMatches.length}
              </div>
            )}
          </div>

          <div className="matches-container card-ios">
            {state.generatedMatches.length === 0 ? (
              <div className="empty-matches">
                <FontAwesomeIcon icon={faDice} className="empty-matches-icon" />
                <div style={{ fontSize: '1.125rem', fontWeight: '500', marginBottom: '0.5rem' }}>
                  {t('noMatchesGenerated')}
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-tertiary)' }}>
                  Click "{t('generate')}" to create matches
                </div>
              </div>
            ) : (
              <div className="matches-list">
                {state.generatedMatches.map((match: Match, index: number) => (
                  <div key={`${match.court.id}-${index}`} className="match-item">
                    <div className="match-court">
                      <div className="court-circle">
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="court-icon" />
                        <div>{t('court_number', { number: match.court.number })}</div>
                      </div>
                    </div>
                    <div className="match-details">
                      <div className="match-type">
                        <FontAwesomeIcon icon={match.isDoubles ? faUsers : faUser} style={{ marginRight: '0.375rem' }} />
                        {match.players.length === 2 ? t('singles') : t('doubles')}
                      </div>
                      <div className="match-players">
                        {match.isDoubles ? (
                          <>
                            <span className="team">
                              <FontAwesomeIcon icon={faUser} style={{ fontSize: '0.75rem', marginRight: '0.25rem' }} />
                              <DraggablePlayer 
                                player={match.players[0]} 
                                matchId={match.id} 
                                playerIndex={0} 
                                isWinner={false}
                                controller={controller}
                              /> 
                              {' & '}
                              <DraggablePlayer 
                                player={match.players[1]} 
                                matchId={match.id} 
                                playerIndex={1} 
                                isWinner={false}
                                controller={controller}
                              />
                            </span>
                            <span className="match-vs">
                              {t('vs')}
                            </span>
                            <span className="team">
                              <FontAwesomeIcon icon={faUser} style={{ fontSize: '0.75rem', marginRight: '0.25rem' }} />
                              <DraggablePlayer 
                                player={match.players[2]} 
                                matchId={match.id} 
                                playerIndex={2} 
                                isWinner={false}
                                controller={controller}
                              />
                              {' & '}
                              <DraggablePlayer 
                                player={match.players[3]} 
                                matchId={match.id} 
                                playerIndex={3} 
                                isWinner={false}
                                controller={controller}
                              />
                            </span>
                          </>
                        ) : (
                          <>
                            <span className="team">
                              <FontAwesomeIcon icon={faUser} style={{ fontSize: '0.75rem', marginRight: '0.25rem' }} />
                              <DraggablePlayer 
                                player={match.players[0]} 
                                matchId={match.id} 
                                playerIndex={0} 
                                isWinner={false}
                                controller={controller}
                              />
                            </span>
                            <span className="match-vs">
                              {t('vs')}
                            </span>
                            <span className="team">
                              <FontAwesomeIcon icon={faUser} style={{ fontSize: '0.75rem', marginRight: '0.25rem' }} />
                              <DraggablePlayer 
                                player={match.players[1]} 
                                matchId={match.id} 
                                playerIndex={1} 
                                isWinner={false}
                                controller={controller}
                              />
                            </span>
                          </>
                        )}
                      </div>
                      {/* Add Result Button or Result Status */}
                      <div className="match-result-action">
                        {(() => {
                          const existingResult = controller.getMatchResult(match.id);
                          return existingResult ? (
                            <div className="result-recorded">
                              <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                              {t('resultRecorded')}
                            </div>
                          ) : (
                            <button className="btn btn-outline-primary btn-sm" onClick={() => handleOpenResultModal(match)}>
                              <FontAwesomeIcon icon={faEdit} className="me-1" />
                              {t('recordResult')}
                            </button>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          <button 
            className="btn-enhanced btn-primary-enhanced"
            onClick={handleGenerateMatches}
            disabled={activePlayersCount < 2}
            title={activePlayersCount < 2 ? (t('needAtLeastTwoPlayers') as string) : state.generatedMatches.length > 0 ? (t('shuffleAndGenerate') as string) : (t('generateMatches') as string)}
          >
            <FontAwesomeIcon icon={faWandMagicSparkles} />
            {t('generate')}
          </button>
          
          <button 
            className="btn-enhanced btn-secondary-enhanced"
            onClick={handleResetMatches}
            disabled={state.generatedMatches.length === 0}
            title={state.generatedMatches.length === 0 ? (t('noMatchesToReset') as string) : (t('clearAllMatches') as string)}
          >
            <FontAwesomeIcon icon={faUndo} />
            {t('reset')}
          </button>
        </div>
      </div>
    </div>
  );
}; 